import axios from "axios";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleLogout = () => {
  localStorage.clear();
  toast.error("Session expired. Please log in again.");
  window.location.href = "/login";
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const isUnauthorized = error.response?.status === 401;
    const isRetry = originalRequest._retry;
    const refreshToken = localStorage.getItem("refreshToken");

    if (isUnauthorized && !isRetry && refreshToken) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const refreshAxios = axios.create({
          baseURL: API_BASE_URL,
        });
        refreshAxios.interceptors.request.use((config) => {
          if (refreshToken) {
            config.headers["Authorization"] = `Bearer ${refreshToken}`;
          }
          return config;
        });
        const refreshResponse = await axios.post(
          `${API_BASE_URL}/user/reauthenticate`,
          {},
          {
            headers: { Authorization: `Bearer ${refreshToken}` },
          },
        );

        const newToken = refreshResponse.data.data.accessToken;
        if (!newToken) {
          throw new Error("No access token received from refresh");
        }
        localStorage.setItem("token", newToken);
        processQueue(null, newToken);

        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (err) {
        processQueue(err, null);
        if (err.response?.status === 401 || err.response?.status === 403) {
          handleLogout();
        } else {
          toast.error("Something went wrong during session refresh.");
        }
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
